import { acceptHMRUpdate, defineStore } from 'pinia'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { useCustomers } from './customers'
import {
    assignRegionRole,
    assignRole,
    getRoleOptions,
    getUserRegions,
    revokeRegionRole,
    revokeRole,
    getUsers,
    inviteUser,
    createNewOrganization
} from '../scripts/customers'
import { getVehicles } from '../scripts/task'
import i18n from '@/i18n'
import { removeUnderline } from '@/helpers/stringHelper'
import { useUserRole } from './userRole'
import { useKeycloak } from '@dsb-norge/vue-keycloak-js'
import { postSetVehicleMembership, postTriggerResync } from '../scripts/task'
import { useVehicleManagementStore } from './vehicleManagement'
import { DEFAULT_TRACK_WIDTH } from './drawing'

// UI labels only, the backend decides which roles are actually selectable
export const ROLE_LABELS = {
    nexat_admin: 'Nexat Admin',
    nexat_service: 'Nexat Service',
    nexat_view: 'Nexat View',
    customer_admin: 'Customer Admin',
    customer_edit: 'Customer Edit',
    customer_view: 'Customer View',
    sync: 'Sync'
}

export const HEADLAND_CONFIG_OPTIONS = [
    { label: '[1, 1, 1]', value: [1, 1, 1] },
    { label: '[1, 0.5, 1, 1]', value: [1, 0.5, 1, 1] },
    { label: '[1, 1, 0.5, 0.5, 1]', value: [1, 1, 0.5, 0.5, 1] },
]

export const useUsersManagementStore = defineStore('usersManagement', {
    state: () => ({
        users: [],
        selectedUser: null,
        organizationDialogVisible: false,
        vehicleDialogVisible: false,
        inviteUserDialogVisible: false,
        newOrgDialogVisible: false,
        newOrg: {
            organization_name: '',
            region_id: null,
            unit_system: 'metric',
            default_track_width: DEFAULT_TRACK_WIDTH,
            default_headland_config: HEADLAND_CONFIG_OPTIONS[0]
        },
        selectedOrganization: null,
        newUser: {
            name: '',
            email: '',
            unit_system: 'metric'
        },
        unitSystems: [
            { label: i18n.global.t('Metric'), value: 'metric' },
            { label: i18n.global.t('Imperial'), value: 'imperial' }
        ],
        roles: [],
        canAssignRole: false,
        canRevokeRole: false,
        loadingRoleOptions: false,
        searchQuery: '',
        filters: null,
        loading: false,
        loadingRegions: false,
        remainingFarms: [],
        assignmentMode: 'organization',
        availableRegions: [],
        userRegionMemberships: [],
        selectedFarm: {
            farm_id: null,
            kc_id: null,
            role: null,
            valid_from: null,
            valid_to: null
        },
        selectedRegion: {
            region_id: null,
            kc_id: null,
            role: null,
            valid_from: null,
            valid_to: null
        },
        originalOrganization: null,
        selectedRegionOriginalRole: null,
        vehicles: {},
        otherWords: [
            i18n.global.t('No vehicles'),
            i18n.global.t('No organizations'),
            'N/A'
        ],
        selectedVehicle: null,
        tableInfoDialogVisible: false
    }),
    getters: {
        filteredUsers(state) {
            const query = state.searchQuery?.trim()
            if (!query) {
                return state.users
            }

            const q = query.toLowerCase()

            return state.users.filter(user =>
                user.name.toLowerCase().includes(q) ||
                user.mail.toLowerCase().includes(q) ||
                i18n.global.t(user.unit_system).toLowerCase().includes(q) ||
                user.organizations.some(org =>
                    org.organization_id.toString().includes(query) ||
                    removeUnderline(org.role).toLowerCase().includes(q) ||
                    this.getOrganization(org.organization_id)?.name?.toLowerCase().includes(q)
                ) ||
                (user.vehicles && user.vehicles.some(vehicle =>
                    vehicle.vehicle_serial.toString().includes(query) ||
                    this.getVehicleOrganizationName(vehicle).toLowerCase().includes(q)
                )) ||
                this.formatDate(user.created_at).toLowerCase().includes(q) ||
                ((user.access?.region_membership?.length > 0) && user.access.region_membership.some(region =>
                    region.region_name?.toLowerCase().includes(q)
                )) ||
                ((user.access?.organization_membership?.length > 0) && user.access.organization_membership.some(organization =>
                    organization.organization_name?.toLowerCase().includes(q)
                )) ||
                // Search for otherWords - for users without vehicles or organizations
                ((!user.vehicles || user.vehicles.length === 0) && state.otherWords.some(word =>
                    word.toLowerCase().includes(q)
                )) ||
                ((!user.organizations || user.organizations.length === 0) && state.otherWords.some(word =>
                    word.toLowerCase().includes(q)
                )) ||
                user.organizations.some(org => org.region_org_overridden && i18n.global.t('Overridden').toLowerCase().includes(q)) ||
                user.organizations.some(org => (org.regions || []).length === 0 && i18n.global.t('Direct organizations').toLowerCase().includes(q))
            )
        }
    },
    actions: {
        normalizeUserOrganizations(user) {
            const byOrgId = new Map()

            const regionMembership = user.access?.region_membership || []
            for (const region of regionMembership) {
                const regionInfo = {
                    region_id: region.region_id,
                    name: region.region_name ?? region.name
                }

                for (const organization of region.organizations || []) {
                    const orgId = organization.organization_id
                    if (orgId === undefined || orgId === null) {
                        continue
                    }

                    if (!byOrgId.has(orgId)) {
                        byOrgId.set(orgId, {
                            organization_id: orgId,
                            organization_name: organization.organization_name ?? organization.name,
                            role: null,
                            valid_from: null,
                            valid_to: null,
                            from_region: null,
                            regions: [],
                            region_org_overridden: false,
                            from_region_role: null
                        })
                    }

                    const current = byOrgId.get(orgId)
                    if (!current.organization_name && (organization.organization_name || organization.name)) {
                        current.organization_name = organization.organization_name ?? organization.name
                    }

                    if (regionInfo.region_id !== undefined && regionInfo.region_id !== null) {
                        const exists = current.regions.some(existingRegion => existingRegion.region_id === regionInfo.region_id)
                        if (!exists) {
                            current.regions.push(regionInfo)
                        }
                    }

                    if (!current.from_region_role && region.role) {
                        current.from_region_role = region.role
                    }

                    const isOverridden = Boolean(organization.region_org_overridden)
                    if (isOverridden) {
                        current.region_org_overridden = true
                        if (organization.organization_role) {
                            current.role = organization.organization_role
                            current.from_region = null
                            current.valid_from = organization.organization_valid_from
                            current.valid_to = organization.organization_valid_to
                        }
                    } else if (!current.role || current.from_region !== null) {
                        current.role = region.role
                        current.valid_from = region.valid_from
                        current.valid_to = region.valid_to
                        current.from_region = regionInfo.region_id
                    }
                }
            }

            for (const organization of user.access?.organization_membership || []) {
                const orgId = organization.organization_id
                if (orgId === undefined || orgId === null) {
                    continue
                }

                if (!byOrgId.has(orgId)) {
                    byOrgId.set(orgId, {
                        organization_id: orgId,
                        organization_name: organization.organization_name ?? organization.name,
                        role: null,
                        valid_from: null,
                        valid_to: null,
                        from_region: null,
                        regions: [],
                        region_org_overridden: false,
                        from_region_role: null
                    })
                }

                const current = byOrgId.get(orgId)
                if (!current.organization_name && (organization.organization_name || organization.name)) {
                    current.organization_name = organization.organization_name ?? organization.name
                }

                // Membership direct from organization has priority.
                current.role = organization.role
                current.valid_from = organization.valid_from
                current.valid_to = organization.valid_to
                current.from_region = null
            }

            return Array.from(byOrgId.values())
        },
        async loadUsers() {
            try {
                this.availableRegions = []
                const usersList = await getUsers()

                if (usersList && usersList.length > 0) {
                    this.users = usersList.map(user => ({
                        ...user,
                        organizations: this.normalizeUserOrganizations(user),
                        created_at: user.created_at ?? null
                    }))
                } else {
                    this.users = []
                }
                return true
            } catch (error) {
                console.error(error)
                return false
            }
        },
        clearFilter() {
            this.initFilters()
            this.searchQuery = ''
        },
        initFilters() {
            this.filters = {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS },
                name: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] 
                },
                mail: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] 
                },
                unit_system: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] 
                },
                created_at: { 
                    operator: FilterOperator.AND, 
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] 
                },
            }
        },
        formatDate(dateString) {
            if (!dateString) return '-';
            return new Date(dateString).toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        },
        editElement(entity, user, detailsMode = 'organization') {
            let org = entity
            if (detailsMode === 'region' && !entity?.organization_id && Array.isArray(entity?.organizations)) {
                const baseOrganization = entity.organizations[0]
                if (!baseOrganization) {
                    return false
                }
                org = {
                    ...baseOrganization,
                    role: entity.role ?? baseOrganization.role ?? null,
                    valid_from: entity.valid_from ?? baseOrganization.valid_from ?? null,
                    valid_to: entity.valid_to ?? baseOrganization.valid_to ?? null,
                    from_region: entity.region_id ?? baseOrganization.from_region ?? null,
                    regions: [{ region_id: entity.region_id ?? null, name: entity.name ?? null }]
                }
            }
            const organization = this.getOrganization(org.organization_id)
            if ((!organization || !organization.name) && !org.organization_name) {
                return false
            }
            
            // Save the original organization for cancel
            this.originalOrganization = { ...org }
            this.selectedOrganization = { ...org }
            this.selectedUser = user
            this.assignmentMode = detailsMode === 'region' ? 'region' : 'organization'
            
            // The selectable roles are loaded from the backend once the dialog is shown
            this.resetRoles()

            this.organizationDialogVisible = true
            return true
        },
        setSelectedRegionFromList(region) {
            if (!region) {
                this.selectedRegion = {
                    region_id: null,
                    kc_id: this.selectedUser?.kc_id ?? null,
                    role: null,
                    valid_from: null,
                    valid_to: null
                }
                this.selectedRegionOriginalRole = null
                return
            }

            this.selectedRegion = region
            this.selectedRegion.region_id = region.region_id ?? null
            this.selectedRegion.kc_id = this.selectedUser?.kc_id ?? null
            this.selectedRegion.role = region.role ?? this.selectedOrganization?.role ?? null
            this.selectedRegion.valid_from = region.valid_from ?? this.selectedOrganization?.valid_from ?? null
            this.selectedRegion.valid_to = region.valid_to ?? this.selectedOrganization?.valid_to ?? null
            this.selectedRegionOriginalRole = this.selectedRegion.role
        },
        getNormalizedRegionMemberships(user) {
            const regionMembership = user?.access?.region_membership || []
            const normalized = []
            const seen = new Set()

            for (const region of regionMembership) {
                const item = {
                    ...region,
                    region_id: region.region_id,
                    name: region.name ?? region.region_name
                }
                const key = String(item.region_id ?? item.name)
                if (!key || seen.has(key)) {
                    continue
                }
                seen.add(key)
                normalized.push(item)
            }

            return normalized
        },
        loadRegionDetailsForSelectedUser() {
            if (!this.selectedUser?.kc_id) {
                this.userRegionMemberships = []
                this.setSelectedRegionFromList(null)
                return
            }

            const userRegions = this.getNormalizedRegionMemberships(this.selectedUser)
            this.userRegionMemberships = userRegions
            let preferredRegion = null
            const selectedFromRegionId = this.selectedOrganization?.from_region
            if (selectedFromRegionId != null) {
                preferredRegion = userRegions.find(region => region.region_id === selectedFromRegionId) ?? null
            }
            if (!preferredRegion && Array.isArray(this.selectedOrganization?.regions)) {
                for (const orgRegion of this.selectedOrganization.regions) {
                    const orgRegionId = orgRegion?.region_id
                    preferredRegion = userRegions.find(region => region.region_id === orgRegionId) ?? null
                    if (preferredRegion) {
                        break
                    }
                }
            }
            this.setSelectedRegionFromList(preferredRegion ?? userRegions[0] ?? null)
        },
        getOrganization(orgId) {
            const cs = useCustomers()
            const organization = cs.customers.find(org => org.organization_id === orgId || org.id === orgId)
            return organization
        },
        addOrganization(user) {
            this.selectedUser = user
            this.selectedOrganization = null
            this.assignmentMode = 'organization'
            const cs = useCustomers()

            this.remainingFarms = cs.customers.filter(org => !this.selectedUser.organizations.some(userOrg => userOrg.organization_id === org.id))
            this.selectedFarm = {
                farm_id: null,
                kc_id: user.kc_id,
                role: null,
                valid_from: new Date().toISOString().split('T')[0],
                valid_to: null
            }
            this.selectedRegion = {
                region_id: null,
                kc_id: user.kc_id,
                role: null,
                valid_from: new Date().toISOString().split('T')[0],
                valid_to: null
            }
            
            // Clear previous role options until a target is selected
            this.resetRoles()
            
            this.organizationDialogVisible = true
        },
        setAssignmentMode(mode) {
            this.assignmentMode = mode === 'region' ? 'region' : 'organization'
        },
        async loadRegionsForSelectedUser() {
            if (this.availableRegions.length > 0) return

            try {
                const keycloak = useKeycloak()
                const loggedUserKcId = keycloak?.tokenParsed?.sub
                if (!loggedUserKcId) {
                    this.availableRegions = []
                    return
                }

                this.loadingRegions = true

                const regions = await getUserRegions(loggedUserKcId)
                const regionsList = Array.isArray(regions)
                    ? regions.map(region => ({
                        region_id: region.region_id,
                        name: region.name ?? region.region_name
                    }))
                    : []

                const uniqueRegions = []
                const seen = new Set()
                for (const region of regionsList) {
                    const normalizedRegion = region

                    if (normalizedRegion.region_id === undefined || normalizedRegion.region_id === null) {
                        continue
                    }

                    const key = String(normalizedRegion.region_id ?? normalizedRegion.name)
                    if (!key || seen.has(key)) {
                        continue
                    }

                    seen.add(key)
                    uniqueRegions.push(normalizedRegion)
                }

                this.availableRegions = uniqueRegions
                this.loadingRegions = false
            } catch (error) {
                console.error('Error loading user regions:', error)
                this.availableRegions = []
                this.loadingRegions = false
            }
        },
        getDifferentRegions(user) {
            const existingRegionKeys = new Set()
            const organizations = user?.organizations || []
            for (const organization of organizations) {
                const orgRegions = organization.regions || []
                for (const region of orgRegions) {
                    const key = String(region?.region_id ?? region?.name)
                    existingRegionKeys.add(key)
                }
            }

            const result = []
            for (const region of this.availableRegions || []) {
                const key = String(region?.region_id ?? region?.name)
                if (!key || existingRegionKeys.has(key)) {
                    continue
                }
                result.push(region)
            }
            return result
        },
        resetRoles() {
            this.roles = []
            this.canAssignRole = false
            this.canRevokeRole = false
        },
        async loadRoleOptions({ organizationId = null, regionId = null } = {}) {
            const targetUserId = this.selectedUser?.kc_id
            const hasTarget = organizationId != null || regionId != null

            if (!targetUserId || !hasTarget) {
                this.resetRoles()
                return
            }

            this.loadingRoleOptions = true
            try {
                const options = await getRoleOptions({ targetUserId, organizationId, regionId })
                this.roles = (options?.roles || []).map(role => {
                    const label = ROLE_LABELS[role] || removeUnderline(role)
                    return { label: i18n.global.t(label), value: role }
                })
                this.canAssignRole = options?.can_assign === true
                this.canRevokeRole = options?.can_revoke === true
            } catch (error) {
                console.error('Error loading role options:', error)
                this.resetRoles()
            } finally {
                this.loadingRoleOptions = false
            }
        },
        getAssignmentContext() {
            const isEdit = Boolean(this.selectedOrganization)
            const isRegionMode = this.assignmentMode === 'region'
            const isRegionEdit = isEdit && isRegionMode
            const isRegionAdd = !isEdit && isRegionMode
            const isOrganizationRemove = isEdit && !isRegionMode && this.selectedOrganization?.role === 'remove'
            const isRegionRemove = isRegionEdit && this.selectedRegion?.role === 'remove'

            return {
                isEdit,
                isRegionMode,
                isRegionEdit,
                isRegionAdd,
                isOrganizationRemove,
                isRegionRemove
            }
        },
        buildOrganizationPayload(source) {
            return {
                organization_id: source.organization_id ?? source.id,
                kc_id: this.selectedUser.kc_id,
                role: source.role,
                valid_from: source.valid_from ? new Date(source.valid_from).toISOString() : null,
                valid_to: source.valid_to ? new Date(source.valid_to).toISOString() : null
            }
        },
        buildRegionPayload(source) {
            return {
                region_id: source.region_id,
                kc_id: this.selectedUser.kc_id,
                role: source.role,
                valid_from: source.valid_from ? new Date(source.valid_from).toISOString() : null,
                valid_to: source.valid_to ? new Date(source.valid_to).toISOString() : null
            }
        },
        buildOrganizationRemovePayload() {
            return {
                organization_id: this.selectedOrganization.organization_id,
                kc_id: this.selectedUser.kc_id
            }
        },
        buildRegionRemovePayload() {
            return {
                region_id: this.selectedRegion.region_id,
                kc_id: this.selectedUser.kc_id
            }
        },
        async executeAssignment(context, payload) {
            if (context.isRegionEdit) {
                return context.isRegionRemove
                    ? revokeRegionRole(this.buildRegionRemovePayload())
                    : assignRegionRole(payload)
            }

            if (context.isOrganizationRemove) {
                return revokeRole(payload)
            }

            if (context.isRegionAdd) {
                return assignRegionRole(payload)
            }

            return assignRole(payload)
        },
        async saveOrganization() {
            const context = this.getAssignmentContext()
            let payload = null

            if (context.isOrganizationRemove) {
                payload = this.buildOrganizationRemovePayload()
            } else if (context.isRegionRemove) {
                payload = this.buildRegionRemovePayload()
            } else if (context.isRegionEdit) {
                payload = this.buildRegionPayload(this.selectedRegion)
            } else if (context.isRegionAdd) {
                payload = this.buildRegionPayload(this.selectedRegion)
            } else if (context.isEdit) {
                payload = this.buildOrganizationPayload(this.selectedOrganization)
            } else {
                payload = this.buildOrganizationPayload(this.selectedFarm)
            }
            
            this.organizationDialogVisible = false
            this.loading = true
            try {
                const response = await this.executeAssignment(context, payload)
                if (response?.ok) {
                    await this.loadUsers()
                    await this.loadVehicles()
                    const keycloak = useKeycloak()
                    const currentUserId = keycloak?.tokenParsed?.sub
                    if (this.selectedUser.kc_id === currentUserId) {
                        const userRole = useUserRole()
                        userRole.reset()
                        await userRole.getUserOrgRole(keycloak)
                    }
                    return { success: true, message: null }
                }
                return { success: false, message: this.getResponseMessage(response) }
            } catch (error) {
                console.error('Error saving organization:', error)
                return { success: false, message: error.message }
            } finally {
                this.loading = false
            }
        },
        getResponseMessage(response) {
            const data = response?.data
            const message = data?.info || data?.message || data?.detail
            if (typeof message === 'string') return message
            if (Array.isArray(message)) {
                const first = message[0]?.msg || message[0]?.message
                return typeof first === 'string' ? first : null
            }
            return null
        },
        cancelUpdateOrganization() {
            if (this.selectedOrganization) {
                // Restore the original organization
                this.selectedOrganization = { ...this.originalOrganization }
                this.originalOrganization = null
                this.userRegionMemberships = []
                this.selectedUser = null
                
                // Reset roles to original values
                this.resetRoles()
            } else {
                this.selectedFarm = {
                    farm_id: null,
                    kc_id: null,
                    role: null,
                    valid_from: null,
                    valid_to: null
                }
                this.selectedRegion = {
                    region_id: null,
                    kc_id: null,
                    role: null,
                    valid_from: null,
                    valid_to: null
                }
                this.assignmentMode = 'organization'
                this.userRegionMemberships = []
                this.selectedUser = null
                this.resetRoles()
            }
            this.selectedRegionOriginalRole = null
            this.organizationDialogVisible = false
        },
        getDifferentRoles(user) {
            const cs = useCustomers()
            
            const roles = cs.customers.filter(org => !user.organizations.some(userOrg => userOrg.organization_id === org.id))
            return roles
        },
        isValidObject(obj) {
            return (
                obj && 
                typeof obj === 'object' && 
                !Array.isArray(obj) && 
                Object.keys(obj).length > 0
            )
        },
        async loadVehicles() {
            try {
                const vehicles = await getVehicles()
                if (this.isValidObject(vehicles)) {
                    this.vehicles = vehicles
                    
                    // Add organization_id to each vehicle
                    this.users.forEach(user => {
                        user.vehicles = []
                        user.organizations.forEach(org => {
                            const orgVehicles = this.vehicles[org.organization_id]
                            if (orgVehicles && orgVehicles.length > 0) {
                                const vehiclesWithOrg = orgVehicles.map(vehicle => ({
                                    ...vehicle,
                                    organization_id: org.organization_id
                                }))
                                user.vehicles.push(...vehiclesWithOrg)
                            }
                        })
                    })
                    return true
                } else {
                    this.vehicles = []
                }
            } catch (error) {
                console.error('Error getting vehicles:', error)
                return false
            }
        },
        getVehicleOrganizationName(vehicle) {
            if (vehicle.organization_id) {
                const org = this.getOrganization(vehicle.organization_id)
                return org ? org.name : `Organization ${vehicle.organization_id}`
            }
            return 'Unknown Organization'
        },
        async saveVehicle(vehicle, selectedOrgId, routeName) {
            if (!vehicle.vehicle_serial || !Number.isFinite(selectedOrgId) || !routeName) {
                return false
            }

            this.loading = true
            try {
                const data = {
                    nexat_id: vehicle.vehicle_serial,
                    organization_id: selectedOrgId,
                    skip_resync: false
                }

                const response = await postSetVehicleMembership(data)
                if (response) {
                    if (routeName === 'users-management') {
                        // Reload vehicles for users management component
                        this.vehicles = {}
                        await this.loadVehicles()
                    } else {
                        // Reload vehicles for vehicles management component
                        const vms = useVehicleManagementStore()
                        vms.vehicles = {}
                        await vms.loadVehicles()
                    }
                    return true
                }

                console.error('Error saving vehicle:', response)
                return false
            } catch (error) {
                console.error('Error saving vehicle:', error)
                return false
            } finally {
                this.loading = false
            }
        },
        async triggerResync(vehicle_serial) {
            this.loading = true
            try {
                const response = await postTriggerResync(vehicle_serial)
                if (response) {
                    return true
                }

                console.error('Error triggering resync:', response)
                return false
            } catch (error) {
                console.error('Error triggering resync:', error)
                return false
            } finally {
                this.loading = false
            }
        },
        closeInviteUserDialog() {
            this.inviteUserDialogVisible = false
            this.newUser = {
                name: '',
                email: '',
                unit_system: 'metric'
            }
        },
        closeNewOrgDialog() {
            this.newOrgDialogVisible = false
            this.newOrg = {
                organization_name: '',
                region_id: null,
                unit_system: 'metric',
                default_track_width: DEFAULT_TRACK_WIDTH,
                default_headland_config: HEADLAND_CONFIG_OPTIONS[0]
            }
        },
        async sendInviteUser() {
            if (!this.isNewUserValid()) {
                return { status: 400, data: null }
            }

            const data = {
                name: this.newUser.name.trim(),
                email: this.newUser.email.trim(),
                unit_system: this.newUser.unit_system
            }

            this.closeInviteUserDialog()

            try {
                this.loading = true
                const response = await inviteUser(data)
                if (response.ok) {
                    await this.loadUsers()
                    await this.loadVehicles()
                    return { status: response.status, data: response.data }
                }
        
                return {
                    status: response.status,
                    data: response.data?.info || response.data?.message || response.data?.detail
                }
            } catch (error) {
                console.error('Error sending invite user:', error)
                return { status: 500, data: error.message }
            } finally {
                this.loading = false
            }
        },
        isNewUserValid() {
            switch (true) {
                case !this.newUser.name?.trim():
                    return false
                case !this.newUser.email?.trim():
                    return false
                case !this.newUser.unit_system:
                    return false
                default:
                    return true
            }
        },
        isNewOrgValid() {
            switch (true) {
                case !this.newOrg.organization_name?.trim():
                    return false
                case this.newOrg.region_id == null:
                    return false
                case !this.newOrg.unit_system:
                    return false
                case !this.newOrg.default_track_width:
                    return false
                case !this.newOrg.default_headland_config:
                    return false
                default:
                    return true
            }
        },
        async createOrganization() {
            if (!this.isNewOrgValid()) {
                return { status: 400, data: null }
            }

            const data = {
                organization_name: this.newOrg.organization_name.trim(),
                region_id: this.newOrg.region_id,
                farm: {
                    unit_system: this.newOrg.unit_system,
                    default_track_width: this.newOrg.default_track_width,
                    default_headland_config: this.newOrg.default_headland_config.value
                }
            }

            try {
                this.loading = true
                const response = await createNewOrganization(data)
                if (response.status === 201) {
                    this.closeNewOrgDialog()
                    await this.loadUsers()
                    await this.loadVehicles()

                    const keycloak = useKeycloak()
                    const userRole = useUserRole()
                    userRole.reset()
                    await userRole.getUserOrgRole(keycloak)

                    return { status: response.status, data: response.data }
                }

                return {
                    status: response.status,
                    data: response.data?.info || response.data?.message || response.data?.detail
                }
            } catch (error) {
                console.error('Error creating organization:', error)
                return { status: 500, data: error.message }
            } finally {
                this.loading = false
            }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUsersManagementStore, import.meta.hot))
}
